import { RSAA } from 'redux-api-middleware'
import { unionWith, eqBy, prop, pathOr } from 'ramda'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'
import { makePaginationParams } from 'sidecar-utilities'

import { types, initialState } from './types'
import { filterDuplicateProviders } from './utils'
import { isStaleResponse } from './utils/isStaleResponse'

/**
 * Meta stamped on every phase of a care search so the reducer can tell which
 * search the arriving results belong to.
 *
 * A context key rather than a per-call nonce: results are unioned into the
 * existing list, and `handleLoadMore` must merge into the list it is paging
 * through, so the key has to stay stable across pages of the same search.
 *
 * `memberUuid` belongs in the key alongside the query. Switching who you're
 * searching as re-runs the same query for a different member, and resetting the
 * results first doesn't help — a reset empties the list but doesn't make the
 * previous member's in-flight response identifiable, so it would merge straight
 * back in. Every caller therefore has to pass `memberUuid`, including
 * `handleLoadMore`.
 *
 * @param {string} [query] - The search query the request was made for.
 * @param {string} [memberUuid] - Uuid of the member being searched as.
 * @returns {Object} Meta for an RSAA type descriptor.
 */
const buildSearchContextMeta = (query, memberUuid) => ({
  searchKey: `${memberUuid ?? 'self'}:${query ?? ''}`,
})

export const fetchCareFilters = () => ({
  [RSAA]: {
    endpoint: ({ SearchFilters }) => {
      const searchFilters = SearchFilters?.filters
      const memberUuid =
        (searchFilters?.searchingAs && searchFilters?.searchingAs?.value) ||
        webStorage.getItem('sub')
      return getApiPath('care', `cares/filters?memberUuid=${memberUuid}`)
    },
    method: 'GET',
    types: [
      types.FETCH_CARE_FILTERS_REQUEST,
      types.FETCH_CARE_FILTERS_SUCCESS,
      types.FETCH_CARE_FILTERS_ERROR,
    ],
    cache: true,
  },
})

export const fetchSimpleCareSearch = ({ memberUuid, ...searchParams } = {}) => {
  const params = {
    memberUuid: memberUuid || webStorage.getItem('sub'),
    ...searchParams,
  }

  // Keyed on the resolved memberUuid, not the argument, so a caller that leaves
  // it to `webStorage` lands on the same key as one that passes it explicitly.
  const meta = buildSearchContextMeta(searchParams.query, params.memberUuid)

  return {
    [RSAA]: {
      endpoint: ({ Care: { searchResults } }) => {
        return getApiPath(
          'care',
          `cares/search${makeQueryParams({
            ...params,
            ...makePaginationParams(searchResults),
          })}`,
        )
      },
      method: 'GET',
      types: [
        { type: types.FETCH_CARE_SEARCH_REQUEST, meta },
        { type: types.FETCH_CARE_SEARCH_SUCCESS, meta },
        { type: types.FETCH_CARE_SEARCH_ERROR, meta },
      ],
    },
  }
}

export const resetCareSearch = () => ({
  type: types.RESET_CARE_SEARCH,
})

export const fetchPrecheckedProviders = (
  categoryNames = '',
  insuranceProduct = '',
  ratingArea = '',
) => {
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'care',
        `cares/precheckedProviders${makeQueryParams({
          categoryNames,
          ratingArea,
        })}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_PRECHECKED_PROVIDERS_REQUEST,
        {
          type: types.FETCH_PRECHECKED_PROVIDERS_SUCCESS,
          meta: { categoryNames, insuranceProduct, ratingArea },
        },
        types.FETCH_PRECHECKED_PROVIDERS_ERROR,
      ],
    },
  }
}

export const resetPrecheckedProviders = () => ({
  type: types.RESET_PRECHECKED_PROVIDERS,
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.FETCH_CARE_FILTERS_REQUEST: {
      return {
        ...state,
        careFilters: {
          ...state.careFilters,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_CARE_FILTERS_SUCCESS: {
      return {
        ...state,
        careFilters: {
          ...state.careFilters,
          busy: false,
          filters: payload,
        },
      }
    }
    case types.FETCH_CARE_FILTERS_ERROR: {
      return {
        ...state,
        careFilters: {
          ...state.careFilters,
          busy: false,
        },
      }
    }
    case types.FETCH_CARE_SEARCH_REQUEST: {
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: true,
          meta: meta,
          requestKey: meta.searchKey ?? null,
          error: '',
        },
      }
    }
    case types.FETCH_CARE_SEARCH_SUCCESS: {
      // Results for a query — or a member — the user has moved on from must not
      // be unioned into the list they're looking at now (BASH-100).
      if (isStaleResponse(meta.searchKey, state.searchResults.requestKey)) {
        return state
      }

      const { content, page, last } = payload
      const isLastPageReached = last || content.length === 0
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          page: page,
          busy: false,
          isLastPageReached: isLastPageReached,
          // Merge new search results with existing ones,
          // removing duplicates by uuid.
          entities: unionWith(
            eqBy(prop('uuid')),
            state.searchResults.entities,
            [...content],
          ),
        },
      }
    }

    case types.FETCH_CARE_SEARCH_ERROR: {
      if (isStaleResponse(meta.searchKey, state.searchResults.requestKey)) {
        return state
      }

      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          error: pathOr(
            'Unable to search for care right now. Please try again later.',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }

    case types.RESET_CARE_SEARCH: {
      return {
        ...state,
        searchResults: {
          ...initialState.searchResults,
        },
      }
    }

    case types.FETCH_PRECHECKED_PROVIDERS_REQUEST: {
      return {
        ...state,
        precheckedProviders: {
          ...state.precheckedProviders,
          busy: true,
          error: '',
        },
      }
    }
    case types.FETCH_PRECHECKED_PROVIDERS_SUCCESS: {
      // Filter out duplicate providers
      const preCheckedProviders = filterDuplicateProviders(payload)
      return {
        ...state,
        precheckedProviders: {
          ...state.precheckedProviders,
          busy: false,
          entities: preCheckedProviders,
        },
      }
    }
    case types.FETCH_PRECHECKED_PROVIDERS_ERROR: {
      return {
        ...state,
        precheckedProviders: {
          ...state.precheckedProviders,
          busy: false,
          error: pathOr(
            'Something went wrong when fetching pre-checked providers',
            ['response', 'message'],
            payload,
          ),
        },
      }
    }
    case types.RESET_PRECHECKED_PROVIDERS: {
      return {
        ...state,
        precheckedProviders: {
          ...initialState.precheckedProviders,
        },
      }
    }

    default:
      return state
  }
}
